import { explicitDocumentDateIso, patientFileListName } from '@/lib/patientFileLabel';

export type PatientFileSortKey = 'name' | 'date' | 'documentDate';
export type PatientFileSortDir = 'asc' | 'desc';

export interface PatientFileSort {
  key: PatientFileSortKey;
  dir: PatientFileSortDir;
}

export const DEFAULT_PATIENT_FILE_SORT: PatientFileSort = { key: 'date', dir: 'desc' };

export const PATIENT_FILE_SORT_STORAGE_PREFIX = 'clinio-v2-patient-files-sort';

export function patientFileSortStorageKey(userId: string | null | undefined): string {
  return userId
    ? `${PATIENT_FILE_SORT_STORAGE_PREFIX}:${userId}`
    : PATIENT_FILE_SORT_STORAGE_PREFIX;
}

export function isPatientFileSortKey(value: unknown): value is PatientFileSortKey {
  return value === 'name' || value === 'date' || value === 'documentDate';
}

export function isPatientFileSortDir(value: unknown): value is PatientFileSortDir {
  return value === 'asc' || value === 'desc';
}

/**
 * Clicking the active column flips direction. Clicking the other column
 * starts at A→Å or oldest first.
 */
export function nextPatientFileSort(
  current: PatientFileSort,
  key: PatientFileSortKey,
): PatientFileSort {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: 'asc' };
}

export function parsePatientFileSort(raw: string | null | undefined): PatientFileSort {
  if (!raw) return DEFAULT_PATIENT_FILE_SORT;
  try {
    const parsed = JSON.parse(raw) as { key?: unknown; dir?: unknown };
    if (!isPatientFileSortKey(parsed.key) || !isPatientFileSortDir(parsed.dir)) {
      return DEFAULT_PATIENT_FILE_SORT;
    }
    return { key: parsed.key, dir: parsed.dir };
  } catch {
    return DEFAULT_PATIENT_FILE_SORT;
  }
}

export function readPatientFileSort(userId: string | null | undefined): PatientFileSort {
  try {
    return parsePatientFileSort(localStorage.getItem(patientFileSortStorageKey(userId)));
  } catch {
    return DEFAULT_PATIENT_FILE_SORT;
  }
}

export function writePatientFileSort(
  userId: string | null | undefined,
  sort: PatientFileSort,
): void {
  try {
    localStorage.setItem(patientFileSortStorageKey(userId), JSON.stringify(sort));
  } catch {
    // private mode / quota
  }
}

export function fileDocumentDate(row: {
  document_date?: string | null;
  created_at: string;
}): string {
  if (row.document_date && row.document_date.length >= 10) {
    return row.document_date.slice(0, 10);
  }
  return row.created_at.slice(0, 10);
}

export function comparePatientFiles(
  a: {
    id: string;
    original_filename: string;
    created_at: string;
    document_date?: string | null;
    document_title?: string | null;
  },
  b: {
    id: string;
    original_filename: string;
    created_at: string;
    document_date?: string | null;
    document_title?: string | null;
  },
  sort: PatientFileSort,
): number {
  let cmp = 0;
  switch (sort.key) {
    case 'name':
      cmp = patientFileListName(a).localeCompare(patientFileListName(b), 'da', {
        sensitivity: 'base',
      });
      break;
    case 'date':
      cmp = fileDocumentDate(a).localeCompare(fileDocumentDate(b));
      if (cmp === 0) cmp = a.created_at.localeCompare(b.created_at);
      break;
    case 'documentDate': {
      const left = explicitDocumentDateIso(a);
      const right = explicitDocumentDateIso(b);
      if (left && right) {
        cmp = left.localeCompare(right);
      } else if (left || right) {
        // Undated rows stay last when the direction flips.
        return left ? -1 : 1;
      }
      break;
    }
    default: {
      const _exhaustive: never = sort.key;
      return _exhaustive;
    }
  }
  if (cmp === 0) cmp = a.id.localeCompare(b.id);
  return sort.dir === 'asc' ? cmp : -cmp;
}

export function sortPatientFiles<
  T extends {
    id: string;
    original_filename: string;
    created_at: string;
    document_date?: string | null;
    document_title?: string | null;
  },
>(rows: readonly T[], sort: PatientFileSort): T[] {
  return [...rows].sort((left, right) => comparePatientFiles(left, right, sort));
}
